import { List, ListItemButton, ListItemIcon, Typography, Box, Divider, Button } from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FormatListBulleted, Receipt } from "@trainingpeaks/designtokens";
import { APP_BASE_URL } from "@config";
import { getTopNavRoutes } from "@constants/topNavRoutes";
import { useSubscriptionContext } from "@context/SubscriptionContext";
import { useUserContext } from "@context/UserContext";
import { SUBSCRIPTION_PRODUCT_TYPES } from "@constants/subscriptions";
import useHandleDeleteAccount from "@hooks/useHandleDeleteAccount";
import useSegment from "@hooks/useSegment";
import { ANALYTICS_EVENTS, ANALYTICS_LOCATIONS } from "@appTypes/analytics";
export interface NavRoute {
  title: string;
  route: string;
  icon?: React.ReactNode;
  key: string;
  segmentEventFeatureName?: string;
}

interface SideNavContentProps {
  isMobile?: boolean;
  location: ReturnType<typeof useLocation>;
  onNavClick?: () => void;
}

const SideNavContent = ({ isMobile, location, onNavClick }: SideNavContentProps) => {
  const { t } = useTranslation();
  const { trackEvent } = useSegment();
  const { handleDeleteAccountOpen, modal } = useHandleDeleteAccount();
  const { subscriptions, areSubscriptionsLoading } = useSubscriptionContext();
  const { user, isUserLoading } = useUserContext();

  const isCoached = user?.settings?.account?.isCoached ?? false;
  const hasCoachMatchSubscription = subscriptions.some(
    (sub) => sub.subscriptionType === SUBSCRIPTION_PRODUCT_TYPES.COACH_MATCH
  );

  const sideNavRoutes: NavRoute[] = [
    {
      title: t("Subscriptions"),
      route: "/subscriptions",
      icon: <FormatListBulleted size="24" />,
      key: "subscriptions-link-item",
      segmentEventFeatureName: "Subscriptions Tab button clicked",
    },
    {
      title: t("Purchases"),
      route: "/purchases",
      icon: <Receipt size="24" />,
      key: "purchases-link-item",
      segmentEventFeatureName: "Purchases Tab button clicked",
    },
  ];

  const additionalMobileNavRoutes: NavRoute[] = getTopNavRoutes(t);

  const renderRouteList = (routeList: NavRoute[]) =>
    routeList.map((route) => {
      const isExternal = route.route.startsWith("http");
      const isSelected =
        !isExternal && (
          location.pathname === route.route ||
          (location.pathname === "/" && route.route === "/subscriptions")
        );

      return (
        <ListItemButton
          key={route.key}
          className="h-12 px-2 rounded w-full"
          {...(isExternal
            ? { component: "a", href: route.route }
            : { component: NavLink as React.ElementType, to: route.route }
          )}
          disableRipple
          variant={"sideNav" as never}
          selected={isSelected}
          onClick={() => {
            if (route.segmentEventFeatureName) {
              trackEvent(
                ANALYTICS_EVENTS.FEATURE_ENGAGED,
                route.segmentEventFeatureName,
                ANALYTICS_LOCATIONS.SIDE_NAVIGATION
              );
            }
            onNavClick?.();
          }}
          color={isSelected ? "primary.main" : "text.primary"}
        >
          <Box className="flex items-center space-x-2">
            {route.icon && (
              <ListItemIcon
                className="min-w-0"
                sx={{
                  color: "inherit",
                  "& svg": {
                    fill: "currentColor"
                  }
                }}
              >
                {route.icon}
              </ListItemIcon>
            )}
            <Typography color="inherit">{route.title}</Typography>
          </Box>
        </ListItemButton>
      );
    });

  return (
    <Box className="flex flex-col h-full">
      {isMobile && APP_BASE_URL ? (
        <>
          <List className="w-full p-0 h-auto mt-2 pl-2">
            {renderRouteList(additionalMobileNavRoutes)}
          </List>
          <Divider className="border-divider-main my-2" />
          <Box className="w-full mb-4 mt-2">
            <Typography variant="h1" className="font-bold text-left pl-4 text-base md:text-2xl" color="text.primary">
              {t("Billing")}
            </Typography>
          </Box>
        </>
      ) : (
        <Box className="w-full mb-4">
          <Typography variant="h1" className="font-bold text-left pl-4 text-base md:text-2xl" color="text.primary">
            {t("Billing")}
          </Typography>
        </Box>
      )}
      <List className={`w-full p-0 h-auto mb-auto ${isMobile ? "pl-6" : "pl-2"}`}>
        {renderRouteList(sideNavRoutes)}
      </List>
      {!isUserLoading && !areSubscriptionsLoading && !hasCoachMatchSubscription && !isCoached && (
        <>
          <Divider className="border-divider-main my-2" />
          <Box className="px-2 pb-4" data-testid="delete-account-button">
            <Button
              variant="text"
              color="error"
              size="small"
              onClick={handleDeleteAccountOpen}
              className="w-full"
            >
              <Typography variant="caption" color="error">
                {t("Delete Your TrainingPeaks Account")}
              </Typography>
            </Button>
          </Box>
          {modal}
        </>
      )}
    </Box>
  );
};

export default SideNavContent;
